import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isWebPushSupported() {
  if (typeof window === "undefined") return false;
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function isIosStandalone() {
  if (typeof window === "undefined") return false;
  const nav = navigator;
  return (
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
    nav.standalone === true
  );
}

export function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export function useWebPush() {
  const supported = isWebPushSupported();
  const [status, setStatus] = useState(supported ? "default" : "unsupported");
  const [busy, setBusy] = useState(false);
  const [publicKey, setPublicKey] = useState(null);
  const [endpoint, setEndpoint] = useState(null);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/notifications/push/vapid-public-key");
        if (cancelled) return;
        setPublicKey(data.public_key);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 503) setStatus("unavailable");
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (cancelled) return;
        if (sub) setEndpoint(sub.endpoint);
        const perm = typeof Notification !== "undefined" ? Notification.permission : "default";
        if (perm === "denied") setStatus("denied");
        else if (perm === "granted") setStatus(sub ? "granted-active" : "granted-inactive");
        else setStatus("default");
      } catch { /* keep default */ }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    if (!publicKey) return { ok: false, reason: "unavailable" };
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setStatus(perm === "denied" ? "denied" : "default");
        return { ok: false, reason: perm };
      }
      const reg =
        (await navigator.serviceWorker.getRegistration()) ||
        (await navigator.serviceWorker.register("/service-worker.js", { scope: "/" }));
      if (!reg.active) {
        await navigator.serviceWorker.ready;
      }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        return { ok: false, reason: "invalid-subscription" };
      }
      await api.post("/notifications/push/subscribe", {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        user_agent: navigator.userAgent.slice(0, 250),
      });
      setEndpoint(json.endpoint);
      setStatus("granted-active");
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: "error" };
    } finally {
      setBusy(false);
    }
  }, [supported, publicKey]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return false;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        try { await sub.unsubscribe(); } catch { /* ignore */ }
        try {
          await api.post("/notifications/push/unsubscribe", { endpoint: sub.endpoint });
        } catch { /* ignore */ }
      }
      setEndpoint(null);
      setStatus("granted-inactive");
      return true;
    } finally {
      setBusy(false);
    }
  }, [supported]);

  return {
    supported, status, busy, endpoint,
    subscribe, unsubscribe,
    isIosSafari: isIosSafari(),
    isIosStandalone: isIosStandalone(),
  };
}
